import { Alert, Box, Button, Checkbox, Divider, Group, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';

import type { CreativeMediaItem } from '../../../data/media';
import { adjustToMonday, isMonday } from '../../../lib/date-utils';
import { preventEnterSubmit } from '../../../lib/utils';

export type EditCreativeMediaFormData = {
    mediaId: string;
    week?: string;
    hasTextOverlay?: boolean;
    hasCallToAction?: boolean;
    additionalText?: string;
};

type EditCreativeMediaFormProps = {
    media: CreativeMediaItem;
    onSubmit: (data: EditCreativeMediaFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
};

export const EditCreativeMediaForm = ({ media, onSubmit, onCancel, isSubmitting }: EditCreativeMediaFormProps) => {
    const form = useForm<EditCreativeMediaFormData>({
        initialValues: {
            mediaId: media.mediaId,
            week: media.marketingFields.week,
            hasTextOverlay: media.marketingFields.hasTextOverlay,
            hasCallToAction: media.marketingFields.hasCallToAction,
            additionalText: media.marketingFields.additionalText || '',
        },
        validate: {
            week: (value) => {
                if (!value) return 'Week is required';
                if (!isMonday(value)) return 'Week must be a Monday';
                return null;
            },
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Only send changed fields
        const updates: EditCreativeMediaFormData = { mediaId: media.mediaId };
        if (form.isDirty('week')) updates.week = values.week;
        if (form.isDirty('hasTextOverlay')) updates.hasTextOverlay = values.hasTextOverlay;
        if (form.isDirty('hasCallToAction')) updates.hasCallToAction = values.hasCallToAction;
        if (form.isDirty('additionalText')) updates.additionalText = values.additionalText;

        onSubmit(updates);
    });

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <Alert color="blue">
                    Editing marketing fields for: <strong>{media.name}</strong>
                </Alert>

                <Box>
                    <DatePickerInput
                        label="Week"
                        required
                        value={form.values.week ? dayjs(form.values.week).toDate() : null}
                        onChange={(date) => {
                            if (date) {
                                form.setFieldValue(
                                    'week',
                                    adjustToMonday(dayjs(date).format('YYYY-MM-DD')).format('YYYY-MM-DD'),
                                );
                            } else {
                                form.setFieldValue('week', '');
                            }
                        }}
                        disabled={isSubmitting}
                        error={form.errors.week}
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                        Date will be automatically adjusted to the nearest Monday
                    </Text>
                </Box>

                <Box>
                    <Checkbox
                        label="Has Text Overlay"
                        disabled={isSubmitting}
                        {...form.getInputProps('hasTextOverlay', { type: 'checkbox' })}
                    />
                    <Text size="xs" c="dimmed" ml="xl" mt={4}>
                        Check if this creative contains text overlay
                    </Text>
                </Box>

                <Box>
                    <Checkbox
                        label="Has Call to Action"
                        disabled={isSubmitting}
                        {...form.getInputProps('hasCallToAction', { type: 'checkbox' })}
                    />
                    <Text size="xs" c="dimmed" ml="xl" mt={4}>
                        Check if this creative has a CTA button
                    </Text>
                </Box>

                <TextInput
                    label="Freeholder (Additional Text)"
                    placeholder="Enter additional text (optional)"
                    disabled={isSubmitting}
                    description="Optional freeform text to include in the creative name"
                    {...form.getInputProps('additionalText')}
                />

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
